<script setup lang="ts">
import type { GroupInList } from '@/types/group';
import type { FollowingGroup } from '@/types/profile';
import type { ButtonGroupItem } from '@/types/button-group';

interface CombinedGroupInfo extends GroupInList {
	isFollowed: boolean;
}

const props = defineProps({
	userId: {
		type: Number as PropType<number | null>,
		default: null,
	},
	profileId: {
		type: Number as PropType<number | null>,
		default: null,
	},
	isPrivateView: {
		type: Boolean,
		default: false,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits<{
	(e: 'onOpenGroupCreationDlg'): void;
	(e: 'onOpenAuthenticationDlg'): void;
	(e: 'onOpenMessageDlg', value: { id: number; followerNum: number }): void;
	(e: 'onUpdateFollowingStatus'): void;
}>();

const profileStore = useProfileStore();
const groupStore = useGroupStore();
const authStore = useAuthStore();
const { isMobile } = useWindowResize();

const filterItem: ButtonGroupItem[] = [
	{
		name: 'Organizing',
		value: GROUP_FILTER.ORGANIZING,
	},
	{
		name: 'Co-organizing',
		value: GROUP_FILTER.CO_ORGANIZING,
	},
];

const currentPage = ref(1);
const items = ref<CombinedGroupInfo[]>([]);
const selectedFilter = ref<string>(filterItem[0].value);

const computedProfileId = computed({
	get: () => props.profileId,
	set: (value) => Number(value),
});

const computedUserId = computed({
	get: () => props.userId,
	set: (value) => Number(value),
});

const fetchId = computed(() => (props.isPrivateView ? Number(props.profileId) : Number(props.userId)));
const totalPages = computed(() => groupStore.groups.paginationInfo?.totalPages);
const isLoading = computed(() => groupStore.isLoading || props.isLoading);
const isOrganizingFilter = computed(() => selectedFilter.value === GROUP_FILTER.ORGANIZING);
const notFoundTitle = computed(() => (isOrganizingFilter.value ? 'No groups found.' : `You’re not Co-organizing any groups.`));
const notFoundDesc = computed(() => (isOrganizingFilter.value
	? 'Your groups will be listed here once you create a group.'
	: 'Groups where you are a Co-organizer will appear here once you’ve been added as one.'));

function getSharedGroupUrl(id: number) {
	return getFullUrlPath(`/group/${id}`);
}

async function fetchFollowingStatus() {
	if (!authStore.isLoggedIn || !computedProfileId.value || !computedUserId.value) {
		return;
	}

	const groupTotalItems = groupStore.groups.paginationInfo?.totalItems;
	await profileStore.getFollowingList(computedProfileId.value, {
		organizer: computedUserId.value,
		itemPerPage: groupTotalItems,
		sortBy: FOLLOWING_SORTING_METHOD.OLDEST_FOLLOWED,
	});
}

async function fetchGroupList() {
	if (fetchId.value) {
		await groupStore.getGroupInfoList(fetchId.value, {
			currentPage: currentPage.value,
			itemPerPage: isMobile.value ? 3 : 6,
			sortBy: GROUP_SORTING_METHOD.OLDEST_ID,
			role: selectedFilter.value,
		});
	}
}

async function fetchData() {
	await fetchGroupList();

	if (!props.isPrivateView) {
		await fetchFollowingStatus();
	}
}

function handleFilterChange() {
	currentPage.value = 1;
	items.value = [];
	fetchData();
}

function handlePageChange(page: number) {
	currentPage.value = page;
	fetchData();
}

function transformGroupListCombinedData(
	groupInfoList: GroupInList[],
	groupFollowingInfoList: FollowingGroup[],
): CombinedGroupInfo[] {
	if (!groupInfoList.length) {
		return [];
	}

	return groupInfoList.map((item) => {
		const isFollowed = groupFollowingInfoList?.find((group) => group.id === item.id);
		return {
			...item,
			isFollowed: !!isFollowed,
		};
	});
}

async function handleUpdateFollowingStatus() {
	await fetchData();
	emit('onUpdateFollowingStatus');
}

onMounted(() => {
	fetchData();
});

watch(
	() => props.profileId,
	(newValue) => {
		if (newValue) {
			// When sign in and got redirect to profile page,
			// Re-fatch data again after receive profile ID
			fetchData();
		}
	},
);

watch(
	() => [groupStore.groups.data, profileStore.followingList],
	([newList, newFollowingList]) => {
		// @ts-ignore
		items.value = transformGroupListCombinedData(newList, newFollowingList);
	},
);

defineExpose({
	fetchGroupList: fetchData,
});
</script>

<template>
	<div class="group-list">
		<div
			v-if="isPrivateView"
			class="header"
		>
			<div class="header-content">
				<h1 class="text-lg text-semibold">
					Groups
				</h1>
				<BaseToggleButtonGroup
					v-model="selectedFilter"
					:items="filterItem"
					@update:model-value="handleFilterChange"
				/>
			</div>
			<BaseButton
				v-if="isOrganizingFilter"
				class="create-group-btn"
				:disabled="isLoading"
				@click="$emit('onOpenGroupCreationDlg')"
			>
				<PlusIcon
					width="20"
					height="20"
				/>
				<p class="text-sm text-semibold">
					Create group
				</p>
			</BaseButton>
		</div>
		<template v-if="items?.length">
			<div class="items">
				<GroupDetailsItem
					v-for="item in items"
					:key="item.id"
					:data="item"
					:is-private-view="isPrivateView"
				>
					<template #miscBtn>
						<div class="misc-btn-container">
							<template v-if="isPrivateView">
								<BaseButton
									variant="subtle"
									color="gray"
									size="sm"
									:disabled="isLoading"
									@click="
										$emit('onOpenMessageDlg', { id: item.id, followerNum: item.followerNumber })
									"
								>
									<template #leftIcon>
										<MailIcon />
									</template>
									<template #tooltip>
										<p>Send message</p>
									</template>
								</BaseButton>
							</template>
							<template v-else>
								<FollowButton
									ref="followBtnRef"
									:user-id="computedProfileId"
									:group-id="item.id"
									:role="item.isFollowed ? GROUP_ROLE.MEMBER : null"
									:is-loading="isLoading"
									:is-logged-in="authStore.isLoggedIn"
									:is-small-size="!isPrivateView"
									@on-update="handleUpdateFollowingStatus"
									@on-sign-in="$emit('onOpenAuthenticationDlg')"
								/>
							</template>
							<ShareButton
								:url="getSharedGroupUrl(item.id)"
								:user-id="isPrivateView ? computedProfileId : computedUserId"
								:group-id="item.id"
								:variant="BtnVariant[2]"
								:gtm-event-name="GTM_EVENT_NAME.SHARE_GROUP_SUCCESS"
								show-only-icon
							/>
						</div>
					</template>

					<template #actionBtn>
						<RouterLink
							:to="{
								name: ROUTE.GROUP.NAME,
								params: {
									id: item.id,
								},
							}"
							target="_blank"
						>
							<BaseButton
								variant="outlined"
								color="gray"
							>
								<p class="text-sm text-semibold">
									View group
								</p>
							</BaseButton>
						</RouterLink>
						<RouterLink
							v-if="isPrivateView"
							:to="{
								name: ROUTE.EVENT_CREATE.NAME,
								query: {
									groupId: item.id,
								},
							}"
							target="_blank"
						>
							<BaseButton>
								<p class="text-sm text-semibold">
									Create event
								</p>
							</BaseButton>
						</RouterLink>
					</template>
				</GroupDetailsItem>
			</div>
		</template>
		<template v-else>
			<MultipleSkeletonLoader
				v-if="isLoading"
				:rows="isMobile ? 3 : 6"
			>
				<ImageSkeletonLoader height="338" />
			</MultipleSkeletonLoader>
			<NoDataFound v-else>
				<template #content>
					<h1 class="text-md text-semibold">
						{{ notFoundTitle }}
					</h1>
					<p class="text-sm text-regular">
						{{ notFoundDesc }}
					</p>
				</template>
			</NoDataFound>
		</template>
		<div class="paginate">
			<BasePagination
				:page="currentPage"
				:total-pages="totalPages"
				:is-loading="isLoading"
				@on-page-change="handlePageChange"
			/>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.group-list {
	margin-top: spacings-get(4);

	.header {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: spacings-get(6);
		margin-bottom: spacings-get(6);

		@include media-query-max(mobile) {
			.create-group-btn {
				width: 100%;
			}
		}

		.header-content {
			width: 100%;
			display: flex;
			align-items: center;
			justify-content: space-between;
		}
	}

	.items,
	:deep(.skeleton-group) {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		grid-template-rows: repeat(2, 1fr);
		row-gap: spacings-get(12) !important;
		column-gap: spacings-get(6) !important;
		padding: 0 rem(2);

		@include media-query-max(mobile) {
			grid-template-columns: repeat(1, 1fr);
			gap: spacings-get(4) !important;
		}

		.misc-btn-container {
			button {
				padding: spacings-get(2);
			}
		}
	}
}
</style>
